/* eslint-disable no-unused-vars */

/**
 * Naming convention: camelCase.
 */

// ====================================================================== //
// Global vars
// ====================================================================== //

export const debounce = require('lodash.debounce');
export const throttle = require('lodash.throttle');

export const iconsFile = '/_assets/dist/img/icons.svg';

export const breakpoints = {
    s: 500,
    m: 700,
    l: 1100
};

export const flags = {
    hoverDisabled: 'has-hover-disabled'
};

/* Site header */
export const siteHeader = document.querySelector('#js-site-header');
export const siteHeaderHeight = siteHeader ? siteHeader.offsetHeight : 0;

// ====================================================================== //
// Dom crawling functions
// ====================================================================== //

export const qSelect = (p, e) => p.querySelector(e);
export const qSelectAll = (p, e) => p.querySelectorAll(e);
export const byId = (e) => document.getElementById(e);
export const byTag = (e, tag) => e.getElementsByTagName(tag);

// ====================================================================== //
// On DOM ready
// ====================================================================== //

/**
 * @const `onReady`
 *
 * A collection of callbacks to be executed when the DOM is ready.
 *
 * @method `add` - attaches the function.
 *    @param `fn` {function}
 * @method `delete` - removes the attached function.
 *    @param `fn` {function}
 * @method `do` - executes the attached functions.
 *
 * @example `onReady.add( myFunction );`
 */
export const onReady = {
    callbacks: new Set(),
    add (fn) {
        this.callbacks.add(fn);
    },
    delete (fn) {
        if (this.callbacks.has(fn)) {
            this.callbacks.delete(fn);
        }
    },
    do () {
        onReady.callbacks.forEach(fn => fn());
    }
};

// ====================================================================== //
// On resize
// ====================================================================== //

/**
 * @const `onResize`
 *
 * A collection of callbacks to be executed when the window is resized.
 *
 * @method `add` - attaches the function.
 *    @param `fn` {function}
 * @method `delete` - removes the attached function.
 *    @param `fn` {function}
 * @method `do` - executes the attached functions.
 *
 * @example `onResize.add( myFunction );`
 */
export const onResize = {
    callbacks: new Set(),
    add (fn) {
        this.callbacks.add(fn);
    },
    delete (fn) {
        if (this.callbacks.has(fn)) {
            this.callbacks.delete(fn);
        }
    },
    do () {
        onResize.callbacks.forEach(fn => fn());
    }
};

// ====================================================================== //
// On scroll
// ====================================================================== //

let lastScrollPosition = window.scrollY;
let scrollPosition = 'down';

/**
 * @function `disableHover`
 *
 * Avoid unnecessary paints through disabling hover
 * effects as the user scrolls.
 */
const disableHover = () => {
    if (!document.body.classList.contains(flags.hoverDisabled)) {
        document.body.classList.add(flags.hoverDisabled);
    }
};

/**
 * @function `enableHover`
 */
const enableHover = () => {
    if (document.body.classList.contains(flags.hoverDisabled)) {
        document.body.classList.remove(flags.hoverDisabled);
    }
};

/**
 * @function `afterScroll`
 *
 * A function to run multiple functions that need to be run when user
 * is done with scrolling.
 */
const afterScroll = debounce(enableHover, 250);

const detectScrollDirection = () => {
    scrollPosition = lastScrollPosition < window.scrollY ? 'down' : 'up';
    lastScrollPosition = window.scrollY;
};

/**
 * @const `onScroll`
 *
 * A collection of callbacks to be executed on scroll. `onScroll.do` is
 *
 * @method `add` - attaches the function.
 *    @param `fn` {function}
 *    @param `throttle` {bool} (optional, default: true)
 * @method `delete` - removes the attached function.
 *    @param `fn` {function}
 * @method `do` - executes the attached functions.
 *
 * @example `onScroll.add( myFunction, false );`
 *
 * 1. The collection of callbacks that are appended without the throttle wrapper.
 * 2. The collection of callbacks that are appended with the throttle wrapper.
 * 3. The function called on scroll without the throttle wrapper.
 * 4. The function called on scroll with the throttle wrapper.
 */
export const onScroll = {
    /* [1] */
    callbacks: new Set(),
    /* [2] */
    throttledCallbacks: new Set(),
    add (fn, throttle = true) {
        this[ throttle ? 'throttledCallbacks' : 'callbacks' ].add(fn);
    },
    delete (fn) {
        if (this.throttledCallbacks.has(fn)) {
            this.throttledCallbacks.delete(fn);
        } else if (this.callbacks.has(fn)) {
            this.callbacks.delete(fn);
        }
    },
    /* [3] */
    do () {
        onScroll.callbacks.forEach(fn => fn());
    },
    /* [4] */
    doThrottle () {
        onScroll.throttledCallbacks.forEach(fn => fn());
    }
};

window.addEventListener('scroll', onScroll.do);
window.addEventListener('scroll', throttle(onScroll.doThrottle, 50));

onScroll.add(detectScrollDirection);
onScroll.add(disableHover);
onScroll.add(afterScroll);

// ====================================================================== //
// Mobile detection
// ====================================================================== //

/**
 * @function `isMobile`
 *
 * Returns `true` if on a mobile device (based on major mobile use agents).
 */
export const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// ====================================================================== //
// SVG
// ====================================================================== //

// onReady.add( svg4everybody );

// ====================================================================== //
// Anchors (animated scroll)
// ====================================================================== //

const anchorParams = {
    anchors: '.js-anchor',
    offset: siteHeaderHeight,
    pixelsPerSecond: 5000
};

/**
 * @function `offsetTop`
 *
 * Gets the difference between the top of the node and his wrapper.
 *
 * @param {HTMLElement} $node - the current node.
 * @param {HTMLElement} $limit (optional, default: body) - the wrapper.
 *
 * @return {int}
 *
 * @example `offsetTop( $myElement, $myParent )`
 */
export const offsetTop = ($node, $limit = document.body) => {
    let $currentNode = $node;
    let offsetTop = 0;

    do {
        offsetTop += $node.offsetTop;
        $currentNode = $currentNode.offsetParent;
    } while (!$currentNode.isEqualNode($limit));

    return offsetTop;
};

/**
 * @function `animationEasing`
 *
 * `easeInOutQuad` operation.
 *
 * @return {int}
 */
const animationEasing = (t, b, c, d) => {
    t /= d / 2;
    if (t < 1) return c / 2 * t * t + b;
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
};

/**
 * @function `animate`
 *
 * Increments a given value on depending of the range and gives it to
 * the function passed as argument that is executed on each iterations
 * of the animation.
 *
 * @param {function} fn - function to execute while animating.
 *     @arg {int} - incremented value while animating.
 * @param {int} from - from value.
 * @param {int} distance - distance to reach.
 * @param {int} duration - duration in milliseconds.
 * @param {object} options - (optional) options.
 *     @method `mode` - easing to use (default: `animationEasing`).
 *     @method `callback` - callback to call after the animation.
 *
 * @example `animate( myFunction, 0, 100, 1000 )`
 *
 * 1. Saves the first timestamp.
 * 2. Saves the current progression.
 * 3. Calculates the next value to iterate and gives it to the called function.
 * 4. Either loops the requestAnimationFrame or executes the callback.
 */
export const animate = (fn, from, distance, duration, options) => {
    let animationInit = null;
    let animationProgress = null;
    let animationNext = null;
    const animationMode = options.mode || animationEasing;
    const animationCallback = options.callback;

    const step = (animationTimestamp) => {
        /* [1] */
        if (!animationInit) animationInit = animationTimestamp;
        /* [2] */
        animationProgress = animationTimestamp - animationInit;
        /* [3] */
        animationNext = animationMode(animationProgress, from, distance - from, duration);
        fn(animationNext);
        /* [4] */
        if (animationProgress < duration) {
            window.requestAnimationFrame(step);
        } else if (animationCallback !== undefined) {
            animationCallback();
        }
    };

    window.requestAnimationFrame(step);
};

/**
 * @function `scrollTo`
 *
 * Scrolls to the target (identified DOM element) of the clicked link.
 *
 * @param {HTMLElement} $link - the clicked link.
 *
 * @example `scrollTo( $triggeredLink )`
 *
 * 1. Returns the absolute result of the substraction as it might be negative
 *    if target is above the anchor.
 * 2. x1000 as the duration has to be defined in milliseconds.
 * 3. Animates the scroll position.
 */
export const scrollTo = ($link) => {
    const $target = document.querySelector($link.getAttribute('href'));
    /* [1] */
    const targetDistance = (offsetTop($target) - anchorParams.offset) - window.scrollY;
    /* [2] */
    const scrollDuration = targetDistance / anchorParams.pixelsPerSecond * 1000;

    /* [3] */
    animate(nextValue => {
        window.scrollTop(0, nextValue);
    }, window.scrollY, targetDistance, scrollDuration);
};
